import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

const SIZE = { md: '', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl' } as const;

export interface ModalProps {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  size?: keyof typeof SIZE;
}

/**
 * Native modal <dialog>: focus is trapped by the browser, Escape closes, and focus returns
 * to the element that opened it. Render it conditionally; it opens on mount.
 */
export function Modal({ title, onClose, children, actions, size = 'md' }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!dialog.open) {
      try {
        dialog.showModal();
      } catch {
        dialog.setAttribute('open', '');
      }
    }
    return () => {
      try {
        dialog.close();
      } catch {
        /* already closed */
      }
      previous?.focus?.();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className="modal modal-bottom sm:modal-middle"
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onCloseRef.current();
      }}
    >
      {/* text-start: the dialog inherits text-align from its DOM parent, which may be a centred or right-aligned cell. */}
      <div className={`modal-box text-start ${SIZE[size]}`}>
        <div className="mb-4 flex items-start justify-between gap-4">
          <h3 id={titleId} className="text-lg font-semibold tracking-tight">
            {title}
          </h3>
          <button type="button" className="btn btn-ghost btn-sm btn-square -mr-2 -mt-1" aria-label="Close dialog" onClick={() => onCloseRef.current()}>
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        {children}
        {actions ? <div className="modal-action flex-wrap">{actions}</div> : null}
      </div>
      <form method="dialog" className="modal-backdrop">
        <button type="button" tabIndex={-1} onClick={() => onCloseRef.current()}>
          close
        </button>
      </form>
    </dialog>
  );
}
